import { Control, UseFormResetField } from 'react-hook-form';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

// @MUI Components
import { Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { Eraser, Plus } from 'mdi-material-ui';

// @Components
import { SearchField, SelectInput } from '../../../components';

// @Interfaces and Types
import { ICustomerDataRequest } from '../../../models';
interface IProps {
  permissions: string[];
  cancelManyTitle: { name: string; ESname: string };
  addBudgetTitle: { name: string; ESname: string };
  status: string;
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  companyByRol: ICustomerDataRequest[];
  typeFilter: number;
  setTypeFilter: Dispatch<SetStateAction<number>>;
  setFilterCustomer: Dispatch<SetStateAction<number | null>>;
  control: Control<{ typeFilter: number; customerFilter: number }>;
  resetField: UseFormResetField<{ typeFilter: number; customerFilter: number }>;
  toggleFilter: () => void;
  handleNavigate: () => void;
  handleCustomerFilter: (value: number) => void;
}
export default function HeaderActions({
  permissions,
  cancelManyTitle,
  addBudgetTitle,
  status,
  value,
  setValue,
  companyByRol,
  typeFilter,
  setTypeFilter,
  setFilterCustomer,
  control,
  resetField,
  toggleFilter,
  handleNavigate,
  handleCustomerFilter,
}: IProps) {
  const { t, i18n } = useTranslation('common');

  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        p: { xs: 2, md: 5 },
        display: 'flex',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'space-between' },
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, height: '36.5px' }}>
        <Box sx={{ height: '36.5px', display: 'flex', flexDirection: 'row' }}>
          <IconButton
            sx={{ height: '36.5px' }}
            color='primary'
            onClick={() => {
              setTypeFilter(2);
              resetField('customerFilter');
              setFilterCustomer(null);
            }}
            disabled={typeFilter === 2}
          >
            <Icon icon='ri:user-search-fill' width={36.5} height={36.5} />
          </IconButton>

          <IconButton
            sx={{ height: '36.5px' }}
            color='primary'
            onClick={() => {
              setTypeFilter(1);
              resetField('customerFilter');
              setFilterCustomer(null);
            }}
            disabled={typeFilter === 1}
          >
            <Icon icon='mdi:file-search' width={36.5} height={36.5} />
          </IconButton>
        </Box>

        {typeFilter === 1 ? (
          <Box sx={{ height: '36.5px', '@media (max-width: 600px)': { height: '20px', width: '150px' } }}>
            <SearchField value={value} handleFilter={setValue} placeholder={t('general.search')} />
          </Box>
        ) : (
          <Box
            sx={{
              ml: '6px',
              width: '270px',
              height: '36.5px',
              '@media (max-width: 600px)': { height: '20px', width: '150px' },
            }}
          >
            <SelectInput
              control={control}
              name='customerFilter'
              label={t('filter.customer')}
              items={companyByRol?.map(el => ({ value: el.id, label: el.name })) || []}
              size='small'
              onExternalChange={e => handleCustomerFilter(e as number)}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', mx: 2, gap: 2, justifyContent: 'flex-end' }}>
        {permissions.includes('cancel_batch') && !['approved', 'cancel', 'finished'].includes(status) && (
          <Button variant='contained' color='error' onClick={toggleFilter} startIcon={!matches && <Eraser />}>
            {!matches ? i18n.language === 'es' ? cancelManyTitle.ESname : cancelManyTitle.name : <Eraser />}
          </Button>
        )}

        {permissions.includes('add') && (
          <Button variant='contained' onClick={handleNavigate} startIcon={!matches && <Plus sx={{ scale: '1.2' }} />}>
            {!matches ? (
              i18n.language === 'es' ? (
                addBudgetTitle.ESname
              ) : (
                addBudgetTitle.name
              )
            ) : (
              <Plus sx={{ scale: '1.2' }} />
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
}
